import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { eur, eur0, fechaCorta, MESES_NOMBRE } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function buildMonthGrid(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // 0 = Monday
  const gridStart = new Date(year, month, 1 - startOffset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  // Drop the trailing week if it's entirely outside the current month.
  const lastWeek = days.slice(35, 42);
  if (lastWeek.every((d) => d.getMonth() !== month)) return days.slice(0, 35);
  return days;
}

export default function Calendario() {
  const { properties, selectedId, selected, selectProperty } = useProperty();
  const navigate = useNavigate();
  const [scopeAll, setScopeAll] = useState(true);
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [payments, setPayments] = useState([]);
  const [vencimientos, setVencimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const propParam = !scopeAll && selectedId ? `?property_id=${selectedId}` : "";

  useEffect(() => {
    if (properties.length === 0) { setLoading(false); return; }
    if (!scopeAll && !selectedId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get(`/payments${propParam}`),
      api.get(`/dashboard${propParam}`),
    ])
      .then(([pRes, dRes]) => {
        setPayments(pRes.data || []);
        setVencimientos(dRes.data.contratos_vigencia || []);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeAll, selectedId, properties.length]);

  const propNombre = (id) => properties.find((p) => p.id === id)?.nombre || "";

  const events = useMemo(() => {
    const map = new Map();
    const add = (key, bucket, item) => {
      if (!key) return;
      if (!map.has(key)) map.set(key, { pendientes: [], cobrados: [], vencimientos: [] });
      map.get(key)[bucket].push(item);
    };
    payments.forEach((p) => add((p.fecha || "").slice(0, 10), p.realizado ? "cobrados" : "pendientes", p));
    vencimientos.forEach((v) => add((v.fecha_fin || "").slice(0, 10), "vencimientos", v));
    return map;
  }, [payments, vencimientos]);

  const days = useMemo(() => buildMonthGrid(monthDate), [monthDate]);
  const todayKey = ymd(new Date());
  const dayDetail = selectedDay ? events.get(selectedDay) : null;

  const changeMonth = (delta) => {
    setMonthDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + delta);
      return d;
    });
  };

  const goToday = () => { const d = new Date(); d.setDate(1); setMonthDate(d); };

  const togglePayment = async (p) => {
    const nuevoValor = !p.realizado;
    const upd = { realizado: nuevoValor, fecha_cobro: nuevoValor ? new Date().toISOString() : null };
    setPayments((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...upd } : x)));
    try {
      await api.put(`/payments/${p.id}`, upd);
    } catch {
      toast.error("Error al actualizar");
      setPayments((prev) => prev.map((x) => (x.id === p.id ? { ...x, realizado: p.realizado, fecha_cobro: p.fecha_cobro } : x)));
    }
  };

  const goToRecord = (propertyId, route) => {
    if (propertyId) selectProperty(propertyId);
    setSelectedDay(null);
    navigate(route);
  };

  if (properties.length === 0) {
    return (
      <div>
        <PageHeader title="Calendario" subtitle="Pagos y vencimientos" />
        <div className="data-card p-10 text-center text-muted-foreground">
          No hay pisos todavía. Crea uno en la sección <strong>Pisos</strong> para empezar.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="calendario-page">
      <PageHeader title="Calendario" subtitle={scopeAll ? "Todos los pisos" : (selected?.nombre || "Selecciona un piso")}>
        <div className="flex flex-wrap gap-2">
          {[[true, "Todos los pisos"], [false, "Piso seleccionado"]].map(([val, label]) => (
            <button
              key={String(val)}
              onClick={() => setScopeAll(val)}
              data-testid={`calendario-scope-${val ? "all" : "property"}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${scopeAll === val ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="data-card p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center justify-center sm:justify-start gap-2">
          <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} data-testid="calendario-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="font-display font-medium text-lg w-40 sm:w-44 text-center capitalize">
            {MESES_NOMBRE[monthDate.getMonth()].toLowerCase()} {monthDate.getFullYear()}
          </p>
          <Button variant="outline" size="sm" onClick={() => changeMonth(1)} data-testid="calendario-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={goToday} data-testid="calendario-today" className="self-center sm:self-auto">
          <CalendarDays className="h-4 w-4 mr-1.5" /> Hoy
        </Button>
        <div className="flex items-center justify-center sm:justify-start gap-3 sm:gap-4 flex-wrap text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warning shrink-0" /> Cobro pendiente</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success shrink-0" /> Cobrado</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary shrink-0" /> Fin de contrato</span>
        </div>
      </div>

      {!scopeAll && !selectedId ? (
        <div className="data-card p-10 text-center text-muted-foreground">
          Selecciona un piso en la cabecera para ver su calendario.
        </div>
      ) : loading ? (
        <Skeleton className="h-[560px] rounded-md" />
      ) : (
        <div className="data-card overflow-hidden">
          <div className="grid grid-cols-7 border-b border-border">
            {DIAS_SEMANA.map((d) => (
              <div key={d} className="px-1 sm:px-2 py-2 text-center text-[11px] sm:text-xs font-medium text-muted-foreground">
                <span className="sm:hidden">{d.slice(0, 1)}</span>
                <span className="hidden sm:inline">{d}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const key = ymd(d);
              const inMonth = d.getMonth() === monthDate.getMonth();
              const isToday = key === todayKey;
              const ev = events.get(key);
              const hasEvents = !!ev && (ev.pendientes.length || ev.cobrados.length || ev.vencimientos.length);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => hasEvents && setSelectedDay(key)}
                  data-testid={`calendario-day-${key}`}
                  className={`min-h-[56px] sm:min-h-[110px] border-b border-r border-border p-1 sm:p-1.5 text-left align-top flex flex-col gap-1 transition-colors ${inMonth ? "bg-card" : "bg-secondary/20"} ${hasEvents ? "hover:bg-secondary/40 cursor-pointer" : "cursor-default"}`}
                >
                  <span className={`text-xs font-mono ${isToday ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>
                    {d.getDate()}
                  </span>
                  {hasEvents && (
                    <span className="flex sm:hidden flex-wrap gap-1 mt-0.5">
                      {ev.pendientes.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-warning shrink-0" />}
                      {ev.cobrados.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-success shrink-0" />}
                      {ev.vencimientos.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                    </span>
                  )}
                  {ev?.pendientes.map((p) => (
                    <span key={p.id} className="hidden sm:flex items-center justify-between gap-1 rounded px-1 py-0.5 text-[11px] font-medium bg-warning/15 text-warning">
                      <span className="truncate">{p.tenant_nombre || "Inquilino"}{scopeAll && propNombre(p.property_id) ? ` · ${propNombre(p.property_id)}` : ""}</span>
                      <span className="font-mono shrink-0">{eur0(p.importe)}</span>
                    </span>
                  ))}
                  {ev?.cobrados.map((p) => (
                    <span key={p.id} className="hidden sm:flex items-center justify-between gap-1 rounded px-1 py-0.5 text-[11px] font-medium bg-success/15 text-success">
                      <span className="truncate">{p.tenant_nombre || "Inquilino"}{scopeAll && propNombre(p.property_id) ? ` · ${propNombre(p.property_id)}` : ""}</span>
                      <span className="font-mono shrink-0">{eur0(p.importe)}</span>
                    </span>
                  ))}
                  {ev?.vencimientos.map((v) => (
                    <span key={v.id} className="hidden sm:flex items-center justify-between gap-1 rounded px-1 py-0.5 text-[11px] font-medium bg-primary/15 text-primary">
                      <span className="truncate">{v.tenant_nombre || "Inquilino"}{v.habitacion ? ` · Hab. ${v.habitacion}` : ""}{scopeAll && v.property_nombre ? ` · ${v.property_nombre}` : ""}</span>
                      <span className="font-mono shrink-0">Fin</span>
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!selectedDay} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{selectedDay ? fechaCorta(selectedDay) : ""}</DialogTitle>
            <DialogDescription>Pagos y vencimientos de este día.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-4">
            {dayDetail?.pendientes.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-warning mb-1.5">Cobros pendientes</p>
                <div className="rounded-md border border-border divide-y divide-border">
                  {dayDetail.pendientes.map((p) => (
                    <div key={p.id} className="p-2.5 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.tenant_nombre || "Inquilino sin nombre"}</p>
                        <p className="text-xs text-muted-foreground truncate">{p.concepto}{scopeAll && propNombre(p.property_id) ? ` · ${propNombre(p.property_id)}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <span className="font-mono text-sm font-semibold text-warning">{eur(p.importe)}</span>
                        <Switch
                          checked={false}
                          onCheckedChange={() => togglePayment(p)}
                          title="Marcar como pagado"
                          data-testid={`calendario-toggle-payment-${p.id}`}
                        />
                        <button
                          onClick={() => goToRecord(p.property_id, "/pagos")}
                          className="p-1 text-muted-foreground hover:text-primary"
                          title="Ver en Pagos"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dayDetail?.cobrados.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-success mb-1.5">Cobrados</p>
                <div className="rounded-md border border-border divide-y divide-border">
                  {dayDetail.cobrados.map((p) => (
                    <div key={p.id} className="p-2.5 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.tenant_nombre || "Inquilino sin nombre"}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {p.concepto}{scopeAll && propNombre(p.property_id) ? ` · ${propNombre(p.property_id)}` : ""}{p.fecha_cobro ? ` · Cobrado el ${fechaCorta(p.fecha_cobro)}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <span className="font-mono text-sm font-semibold text-success">{eur(p.importe)}</span>
                        <Switch
                          checked={true}
                          onCheckedChange={() => togglePayment(p)}
                          title="Desmarcar pago"
                          data-testid={`calendario-toggle-payment-${p.id}`}
                        />
                        <button
                          onClick={() => goToRecord(p.property_id, "/pagos")}
                          className="p-1 text-muted-foreground hover:text-primary"
                          title="Ver en Pagos"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dayDetail?.vencimientos.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-primary mb-1.5">Fin de contrato</p>
                <div className="rounded-md border border-border divide-y divide-border">
                  {dayDetail.vencimientos.map((v) => (
                    <div key={v.id} className="p-2.5 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{v.tenant_nombre || "Inquilino sin nombre"}{v.habitacion ? ` · Hab. ${v.habitacion}` : ""}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {v.fecha_fin_tipo === "inicial" ? "Fin inicial" : "Fin"}{scopeAll && v.property_nombre ? ` · ${v.property_nombre}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <span className="font-mono text-sm font-semibold text-primary">{v.dias_restantes} d</span>
                        <button
                          onClick={() => goToRecord(v.property_id, "/contratos")}
                          className="p-1 text-muted-foreground hover:text-primary"
                          title="Ver en Contratos"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedDay(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
